import {
  CatalogCategoryType,
  FilteredCategoryItemGroup,
} from '@/contexts/item-context-types';
import { CategoryListReturnType, NULL_CATEGORY_VALUE } from '@/utils/category';
import {
  GetVisibleVariantPacksByVariantPackGroupsQuery,
  GetVisibleVariantPacksByVariantPackGroupsQueryVariables,
  ItemsContext_VariantsPackFieldsFragment,
} from '@/types';
import { gql } from '@apollo/client';
import { useAdaptAndGroupVariantPacks } from './use-adapt-and-group-item-list-options';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCatalogFilteringEnabled } from './dynamic-config/use-catalog-filtering-enabled';
import {
  useCatalogVariantPacksQueryVariables,
  VARIANTS_PACK_FRAGMENT,
} from '@/contexts/items-context-queries';
import { useDynamicConfigValue } from './use-dynamic-config-value';
import { useGTINMetadataFilters } from './use-gtin-metadata-filters';
import { useItemQueryVariables } from './use-item-query-variables';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { useWrappedQuery } from './use-wrapped-query';

export const CATEGORY_VARIANT_PACKS_BY_GROUPS_QUERY = gql`
  fragment CatalogCategoryList_GTINFiltersResponse on GetGTINFiltersForCategoryResponse {
    flag_filters
    list_filters {
      brand_names
      free_from_allergens
      origin_countries
      pack_sizes
      subcategories
    }
  }

  query GetVisibleVariantPacksByVariantPackGroups(
    $limit: Int
    $locale: String!
    $offset: Int
    $orderBy: [visible_variant_packs_order_by!]!
    # restaurantUUID is used in the ItemsContext_VariantsPackFields fragment
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $skipCount: Boolean!
    $skipFilters: Boolean!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $variantPackGroupUUID: uuid
    $where: visible_variant_packs_bool_exp!
  ) {
    visible_variant_packs_aggregate(where: $where) @skip(if: $skipCount) {
      aggregate {
        count
      }
    }

    visible_variant_packs(
      limit: $limit
      offset: $offset
      where: $where
      order_by: $orderBy
    ) {
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
    }

    getGtinFiltersForGroup(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_group_id: $variantPackGroupUUID
    ) @skip(if: $skipFilters) {
      ...CatalogCategoryList_GTINFiltersResponse
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export interface CatalogCategoryListProps {
  badgesFilter?: Set<string>;
  category?: CatalogCategoryType;
  selectedItemGroupCategory?: FilteredCategoryItemGroup | null;
  selectedSubcategoryUUIDs?: Set<string>;
}

const PAGE_LIMIT = 500;

/**
 * Decides what subcategory item counts to pass to the filter (Option 5 + modal fix).
 * Exported for unit tests.
 */
export function getSubcategoryItemCountsForFilter(
  isSubcategoryFilterActive: boolean,
  cachedCounts: Map<string, number>,
  countsComplete: boolean,
  currentFilteredCounts: Map<string, number>,
): Map<string, number> | undefined {
  if (isSubcategoryFilterActive) {
    return cachedCounts.size > 0 ? cachedCounts : undefined;
  }
  return countsComplete ? currentFilteredCounts : undefined;
}

export const useCatalogVariantPacksByGroupsList = ({
  category,
  badgesFilter,
  selectedItemGroupCategory,
  selectedSubcategoryUUIDs,
}: CatalogCategoryListProps): CategoryListReturnType => {
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks(false);
  const itemQueryVariables = useItemQueryVariables();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const { isGuestMode } = useUserContext();
  const { visibleVariantPacksOrderBy } = useCatalogVariantPacksQueryVariables(
    supplierUUID!,
  );

  const catalogFilteringEnabled = useCatalogFilteringEnabled();
  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const { childVariantPackGroupMap, parentVariantPackGroupMap } =
    useVariantPackInfoContext();

  const badgeUUIDs = Array.from(badgesFilter || []);
  const subcategoryUUIDs = Array.from(selectedSubcategoryUUIDs || []);

  const badgeFilters = useMemo(() => {
    return {
      ...(itemGroupsEnabled && Boolean(badgeUUIDs.length)
        ? {
            _or: [
              {
                // First condition: Check if the badge UUIDs exist in the direct relationships
                variant_pack_group_memberships: {
                  variant_pack_group_membership: {
                    badge_variant_pack_group_rels: {
                      badge_uuid: {
                        _in: badgeUUIDs,
                      },
                    },
                  },
                },
              },
              {
                // Second condition: Check for nested relationships in variant pack group memberships
                variant_pack_group_memberships: {
                  variant_pack_group_membership: {
                    variant_pack_group_memberships: {
                      variant_pack_group_membership: {
                        badge_variant_pack_group_rels: {
                          badge_uuid: {
                            _in: badgeUUIDs,
                          },
                        },
                      },
                    },
                  },
                },
              },
            ],
          }
        : null),
    };
  }, [itemGroupsEnabled, badgeUUIDs]);

  const queryVariables =
    useMemo<GetVisibleVariantPacksByVariantPackGroupsQueryVariables | null>(() => {
      const selectedCategoryUUID =
        selectedItemGroupCategory?.uuid || category?.uuid;

      if (!selectedCategoryUUID) {
        return null;
      }

      const variantPackGroupUUIDs = [
        selectedCategoryUUID,
        ...(childVariantPackGroupMap[selectedCategoryUUID] || []),
      ];

      return {
        ...itemQueryVariables,
        limit: PAGE_LIMIT,
        orderBy: visibleVariantPacksOrderBy,
        restaurantUUID: restaurantUUID!,
        skipCount: false,
        skipFilters: !catalogFilteringEnabled,
        supplierUUID: supplierUUID!,
        variantPackGroupUUID:
          category?.uuid === NULL_CATEGORY_VALUE ? null : category?.uuid,
        where: {
          ...(category?.name && category?.uuid !== NULL_CATEGORY_VALUE
            ? {
                variant_pack: {
                  variant_pack_group_memberships: {
                    variant_pack_group_uuid: {
                      _in: subcategoryUUIDs.length
                        ? subcategoryUUIDs
                        : variantPackGroupUUIDs,
                    },
                  },
                  ...badgeFilters,
                },
              }
            : {
                _not: {
                  variant_pack: {
                    variant_pack_group_memberships: {
                      variant_pack_group_uuid: { _is_null: false },
                    },
                  },
                },
              }),
          restaurant_uuid: { _eq: restaurantUUID },
          supplier_uuid: { _eq: supplierUUID },
        },
      };
    }, [
      badgeFilters,
      catalogFilteringEnabled,
      category,
      childVariantPackGroupMap,
      itemQueryVariables,
      restaurantUUID,
      selectedItemGroupCategory?.uuid,
      subcategoryUUIDs,
      supplierUUID,
      visibleVariantPacksOrderBy,
    ]);
  const { data, loading, fetchMore } = useWrappedQuery<
    GetVisibleVariantPacksByVariantPackGroupsQuery,
    GetVisibleVariantPacksByVariantPackGroupsQueryVariables
  >(CATEGORY_VARIANT_PACKS_BY_GROUPS_QUERY, {
    context: {
      anonymous: isGuestMode,
    },
    skip: !itemGroupsEnabled || !queryVariables,
    variables: queryVariables!,
  });

  const visibleVPs = useMemo(() => {
    return (data?.visible_variant_packs || []).reduce<
      ItemsContext_VariantsPackFieldsFragment[]
    >((acc, vvp) => {
      const variantPack = vvp.variant_pack;
      if (variantPack) {
        acc.push(variantPack);
      }
      return acc;
    }, []);
  }, [data?.visible_variant_packs]);

  // Cache for subcategory counts - stores counts from when no subcategory filter is active
  // This ensures all non-empty subcategories remain visible for selection even when filtered
  const cachedSubcategoryItemCountsRef = useRef<Map<string, number>>(new Map());
  const lastCategoryUUIDRef = useRef<string | undefined>(undefined);

  // Calculate item counts per subcategory including hierarchy
  const currentSubcategoryItemCounts = useMemo(() => {
    const directCounts = new Map<string, Set<string>>();

    // 1: Count direct items per subcategory
    visibleVPs.forEach((vp) => {
      const memberships = vp.variant_pack_group_memberships || [];
      memberships.forEach((membership) => {
        const subcategoryUUID = membership.variant_pack_group_uuid;
        if (!directCounts.has(subcategoryUUID)) {
          directCounts.set(subcategoryUUID, new Set());
        }
        directCounts.get(subcategoryUUID)!.add(vp.uuid);
      });
    });

    // 2 Propagate counts up the hierarchy
    const totalCounts = new Map<string, Set<string>>();

    // Start with direct counts
    directCounts.forEach((items, subcategoryUUID) => {
      totalCounts.set(subcategoryUUID, new Set(items));
    });

    // Propagate to parents
    directCounts.forEach((items, subcategoryUUID) => {
      const parents = parentVariantPackGroupMap[subcategoryUUID] || [];

      parents.forEach((parentUUID) => {
        if (!totalCounts.has(parentUUID)) {
          totalCounts.set(parentUUID, new Set());
        }
        // Add all items from child to parent
        items.forEach((itemUUID) => {
          totalCounts.get(parentUUID)!.add(itemUUID);
        });
      });
    });

    return totalCounts;
  }, [visibleVPs, parentVariantPackGroupMap]);

  const { gtinFilters, matchingVariantPackUUIDs } = useGTINMetadataFilters(
    visibleVPs,
    catalogFilteringEnabled ?? false,
    data?.getGtinFiltersForGroup,
  );

  // Apply GTIN filters to get current counts
  const currentFilteredCounts = useMemo(() => {
    const filtered = new Map<string, number>();

    currentSubcategoryItemCounts.forEach((itemUUIDs, subcategoryUUID) => {
      if (catalogFilteringEnabled) {
        const visibleItemCount = Array.from(itemUUIDs).filter((itemUUID) =>
          matchingVariantPackUUIDs.has(itemUUID),
        ).length;
        filtered.set(subcategoryUUID, visibleItemCount);
      } else {
        filtered.set(subcategoryUUID, itemUUIDs.size);
      }
    });
    return filtered;
  }, [
    currentSubcategoryItemCounts,
    matchingVariantPackUUIDs,
    catalogFilteringEnabled,
  ]);

  // Update cache when no subcategory filter is active (or category changes)
  // This captures the "true" counts before any subcategory filtering
  // Handle both multi-select (subcategoryUUIDs) and single-select (selectedItemGroupCategory) filters
  const isSubcategoryFilterActive =
    subcategoryUUIDs.length > 0 || Boolean(selectedItemGroupCategory?.uuid);
  const currentCategoryUUID = category?.uuid;

  // Only use subcategory counts for "hide empty" when we have all items loaded.
  // Otherwise (e.g. first page of 500 when total > 500) we'd hide subcategories
  // whose items appear on later pages (e.g. Frozen Appetizers under Specialty).
  const countsComplete = useMemo(() => {
    const currentCount = data?.visible_variant_packs?.length ?? 0;
    const totalCount =
      data?.visible_variant_packs_aggregate?.aggregate?.count ?? 0;
    return currentCount >= totalCount;
  }, [data]);

  useEffect(() => {
    // Reset cache when category changes
    if (currentCategoryUUID !== lastCategoryUUIDRef.current) {
      cachedSubcategoryItemCountsRef.current = new Map();
      lastCategoryUUIDRef.current = currentCategoryUUID;
    }

    // Update cache only when no subcategory filter is active, we have data,
    // and we have complete counts (all items loaded) so we don't cache partial counts
    if (
      !isSubcategoryFilterActive &&
      currentFilteredCounts.size > 0 &&
      countsComplete
    ) {
      cachedSubcategoryItemCountsRef.current = new Map(currentFilteredCounts);
    }
  }, [
    isSubcategoryFilterActive,
    currentFilteredCounts,
    currentCategoryUUID,
    countsComplete,
  ]);

  const filtersAvailable = useMemo(() => {
    return (
      gtinFilters.listFilters.some((f) => f.isAvailable) ||
      gtinFilters.flagFilters.some((f) => f.isAvailable)
    );
  }, [gtinFilters]);

  const fetchMoreOffset = useMemo(() => {
    const currentCount = data?.visible_variant_packs.length ?? 0;
    const totalCount =
      data?.visible_variant_packs_aggregate?.aggregate?.count ?? 0;
    return currentCount < totalCount ? currentCount : null;
  }, [data]);

  const fetchNextPage = useCallback(async () => {
    if (typeof fetchMoreOffset === 'number') {
      await fetchMore<
        GetVisibleVariantPacksByVariantPackGroupsQuery,
        GetVisibleVariantPacksByVariantPackGroupsQueryVariables
      >({
        updateQuery: (prevResult, { fetchMoreResult }) => {
          return fetchMoreResult
            ? {
                ...prevResult,
                visible_variant_packs: [
                  ...prevResult.visible_variant_packs,
                  ...fetchMoreResult.visible_variant_packs,
                ],
              }
            : prevResult;
        },
        variables: {
          ...queryVariables,
          offset: fetchMoreOffset,
          skipCount: true,
        },
      });
    }
  }, [fetchMore, fetchMoreOffset, queryVariables]);

  const listData = useMemo(() => {
    return adaptAndGroupVariantPacks(
      catalogFilteringEnabled
        ? visibleVPs.filter((vp) => matchingVariantPackUUIDs.has(vp.uuid))
        : visibleVPs,
    );
  }, [
    adaptAndGroupVariantPacks,
    catalogFilteringEnabled,
    matchingVariantPackUUIDs,
    visibleVPs,
  ]);

  // Only pass counts when complete so we don't hide subcategories that have
  // items only on later pages (Option 5: gate "hide empty" on full data).
  // When a subcategory filter is active, pass only the cache (full list of
  // non-empty subcategories) so the modal shows all options—never
  // currentFilteredCounts, which would only contain the selected subcategory
  // and cause the modal to show just that one option.
  const subcategoryItemCounts = getSubcategoryItemCountsForFilter(
    isSubcategoryFilterActive,
    cachedSubcategoryItemCountsRef.current,
    countsComplete,
    currentFilteredCounts,
  );

  return useMemo(
    () => ({
      fetchMoreOffset,
      fetchNextPage,
      filtersAvailable,
      gtinFilters,
      listData,
      loading,
      subcategoryItemCounts,
    }),
    [
      filtersAvailable,
      fetchNextPage,
      loading,
      listData,
      gtinFilters,
      fetchMoreOffset,
      subcategoryItemCounts,
    ],
  );
};
